import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { leadsApi, type Lead } from '../api/leads'
import { usersApi, customStatusesApi, customFieldsApi, type CustomField as CustomFieldDef } from '../api/settings'
import { organizationsApi } from '../api/contacts'
import { InlineField, InlineSelectField } from '../components/InlineField'
import { SourceBadge } from '../components/ui/SourceBadge'
import { StatusSelect } from '../components/StatusSelect'
import { ActivityTimeline } from '../components/ActivityTimeline'
import { ComposeArea } from '../components/ComposeArea'
import { recordingsApi } from '../api/recordings'
import { SLATimer } from '../components/SLATimer'
import { ConfirmDialog } from '../components/ui/ConfirmDialog'
import { DropdownSelect } from '../components/ui/DropdownSelect'
import { Modal } from '../components/ui/Modal'
import { PageSpinner } from '../components/ui/Spinner'
import { formatDate, fullName, maskEmail } from '../lib/utils'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  ArrowLeft, Trash2, RefreshCw, Pencil, Check, X, UserMinus, Phone,
  ChevronLeft, ChevronRight,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { startCall, decodeRt } from '../lib/sip'
import { useAuthStore } from '../store/auth'

// ── Assigned-to field ─────────────────────────────────────────────────────────

function AssignedToField({
  value, users, onAssign, onUnassign, readOnly,
}: {
  value: string | null
  users: { id: string; firstName: string; lastName: string }[]
  onAssign: (userId: string) => void
  onUnassign: () => void
  readOnly?: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value ?? '')
  const [confirmUnassign, setConfirmUnassign] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const assignedUser = users.find(u => u.id === value)

  useEffect(() => { if (!editing) setDraft(value ?? '') }, [value, editing])

  useEffect(() => {
    if (!editing) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setDraft(value ?? ''); setEditing(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [editing, value])

  const save = () => { if (draft && draft !== value) onAssign(draft); setEditing(false) }
  const cancel = () => { setDraft(value ?? ''); setEditing(false) }

  const userOptions = users.map(user => ({
    value: user.id,
    label: fullName(user.firstName, user.lastName),
    searchValue: fullName(user.firstName, user.lastName),
  }))

  return (
    <>
      <ConfirmDialog
        isOpen={confirmUnassign}
        onClose={() => setConfirmUnassign(false)}
        onConfirm={onUnassign}
        title="Remove assignment?"
        message="This lead will be left unassigned."
        confirmLabel="Unassign"
      />

      <div className="flex items-start group/field py-1">
        <dt className="w-28 text-xs text-gray-500 font-medium flex-shrink-0 pt-1">Assigned To</dt>
        <dd ref={containerRef} className="flex-1 min-w-0">
          {editing ? (
            <div className="flex items-center gap-1">
              <DropdownSelect
                value={draft}
                onChange={setDraft}
                options={userOptions}
                placeholder="Select user"
                searchable
                className="flex-1"
              />
              <button onMouseDown={e => { e.preventDefault(); save() }} title="Save (Enter)"
                className="p-1 text-green-600 hover:text-green-700 flex-shrink-0">
                <Check className="w-3.5 h-3.5" />
              </button>
              <button onMouseDown={e => { e.preventDefault(); cancel() }} title="Cancel (Esc)"
                className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0">
                <X className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {value ? (
                <>
                  <span className={`text-sm text-gray-900 ${!readOnly ? 'cursor-pointer' : ''}`} onClick={() => !readOnly && setEditing(true)}>
                    {assignedUser ? fullName(assignedUser.firstName, assignedUser.lastName) : value}
                  </span>
                  {!readOnly && (
                    <>
                      <button onClick={e => { e.stopPropagation(); setEditing(true) }}
                        className="opacity-0 group-hover/field:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity">
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button onClick={() => setConfirmUnassign(true)}
                        title="Remove assignment"
                        className="ml-0.5 p-0.5 text-gray-300 hover:text-red-500 transition-colors">
                        <UserMinus className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <span className={`text-sm text-gray-400 italic ${!readOnly ? 'cursor-pointer' : ''}`} onClick={() => !readOnly && setEditing(true)}>
                    Unassigned
                  </span>
                  {!readOnly && (
                    <button onClick={e => { e.stopPropagation(); setEditing(true) }}
                      className="opacity-0 group-hover/field:opacity-100 p-0.5 text-gray-400 hover:text-gray-600 transition-opacity">
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </dd>
      </div>
    </>
  )
}

// ── Notes auto-save field ─────────────────────────────────────────────────────

function NotesField({ companyId, leadId, initialValue }: {
  companyId: string
  leadId: string
  initialValue: string
}) {
  const [value, setValue] = useState(initialValue)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const boxRef = useRef<HTMLTextAreaElement>(null)

  // Grow the box with its content so long notes stay fully visible, and shrink
  // it back when text is removed. The floor and ceiling live in CSS below:
  // min-height keeps the original 5-row box, max-height caps it at roughly 20
  // lines, after which the textarea scrolls on its own.
  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    el.style.height = 'auto' // measure the content, not the current box
    el.style.height = `${el.scrollHeight}px`
  }, [value])

  // Sync if server value changes (e.g. after refetch)
  useEffect(() => { setValue(initialValue) }, [initialValue])

  const handleChange = useCallback((newVal: string) => {
    setValue(newVal)
    if (timerRef.current) clearTimeout(timerRef.current)
    if (abortRef.current) abortRef.current.abort()

    timerRef.current = setTimeout(async () => {
      const ctrl = new AbortController()
      abortRef.current = ctrl
      setSaveState('saving')
      try {
        await leadsApi.update(companyId, leadId, { notes: newVal, skipActivity: true }, ctrl.signal)
        setSaveState('saved')
        setTimeout(() => setSaveState('idle'), 1500)
      } catch (err) {
        if (axios.isCancel(err) || (err as any)?.code === 'ERR_CANCELED') return
        setSaveState('idle')
      }
    }, 250)
  }, [companyId, leadId])

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="section-title">Notes</p>
        {saveState === 'saving' && <span className="text-[11px] text-gray-400">Saving…</span>}
        {saveState === 'saved' && <span className="text-[11px] text-green-600">Saved</span>}
      </div>
      <textarea
        ref={boxRef}
        value={value}
        onChange={e => handleChange(e.target.value)}
        placeholder="Add notes about this lead…"
        className="input resize-none overflow-y-auto text-sm"
        style={{ minHeight: '7.5rem', maxHeight: '26rem' }}
      />
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function LeadDetailPage() {
  const { companyId, leadId } = useParams<{ companyId: string; leadId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const isSalesUser = user?.role === 'sales_user'
  const canSeePhone = !isSalesUser || !!user?.canSeePhone
  const canSeeEmail = !isSalesUser || !!user?.canSeeEmail
  const [convertOpen, setConvertOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // Status the prev/next arrows walk through. Persisted because the arrows
  // navigate to a new route, which remounts this page — component state alone
  // would reset the choice on every click.
  const [navStatus, setNavStatus] = useState<string>(() => {
    try { return localStorage.getItem(`lead-nav-status-${companyId}`) ?? '' } catch { return '' }
  })

  useEffect(() => {
    try { localStorage.setItem(`lead-nav-status-${companyId}`, navStatus) } catch {}
  }, [companyId, navStatus])

  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsApi.get(companyId!, leadId!).then(r => r.data),
    enabled: !!companyId && !!leadId,
  })
  usePageTitle(lead ? fullName(lead.firstName, lead.lastName) || 'Lead' : 'Lead')

  // Counts shown against each status in the nav picker.
  const { data: statusCounts } = useQuery({
    queryKey: ['lead-status-counts', companyId],
    queryFn: () => leadsApi.statusCounts(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const { data: prevData } = useQuery({
    queryKey: ['lead-prev', companyId, leadId, navStatus],
    queryFn: () => leadsApi.prevLead(companyId!, leadId!, navStatus || undefined).then(r => r.data),
    enabled: !!companyId && !!leadId,
  })

  const { data: nextData } = useQuery({
    queryKey: ['lead-next', companyId, leadId, navStatus],
    queryFn: () => leadsApi.nextLead(companyId!, leadId!, navStatus || undefined).then(r => r.data),
    enabled: !!companyId && !!leadId,
  })

  const { data: statuses } = useQuery({
    queryKey: ['custom-statuses', companyId],
    queryFn: () => customStatusesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: users } = useQuery({
    queryKey: ['users', companyId],
    queryFn: () => usersApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  const { data: orgsData } = useQuery({
    queryKey: ['organizations', companyId],
    queryFn: () => organizationsApi.list(companyId!, { limit: 500 }).then(r => r.data.data),
    enabled: !!companyId,
  })

  const { data: customFieldDefs } = useQuery({
    queryKey: ['custom-fields', companyId, 'lead'],
    queryFn: () => customFieldsApi.list(companyId!, 'lead').then(r =>
      r.data.filter((f: CustomFieldDef) => f.isActive)
        .sort((a: CustomFieldDef, b: CustomFieldDef) => a.sortOrder - b.sortOrder)
    ),
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  })

  // Calls + recordings for this lead (drives the timeline play buttons + Calls tab).
  const { data: leadCalls } = useQuery({
    queryKey: ['lead-calls', companyId, leadId],
    queryFn: () => recordingsApi.listLeadCalls(companyId!, leadId!).then(r => r.data.data),
    enabled: !!companyId && !!leadId,
  })

  const orgInList = orgsData?.find(o => o.id === lead?.organizationId)
  const { data: fallbackOrg } = useQuery({
    queryKey: ['organization', companyId, lead?.organizationId],
    queryFn: () => organizationsApi.get(companyId!, lead!.organizationId!).then(r => r.data),
    enabled: !!lead?.organizationId && !!orgsData && !orgInList,
  })

  const invalidateAll = (structural = false) => {
    qc.invalidateQueries({ queryKey: ['lead', leadId] })
    qc.invalidateQueries({ queryKey: ['leads', companyId] })
    if (structural) {
      qc.invalidateQueries({ queryKey: ['dashboard-overview', companyId] })
      // A status change moves a lead between buckets, so the picker's counts
      // are stale until refetched.
      qc.invalidateQueries({ queryKey: ['lead-status-counts', companyId] })
    }
  }

  const patch = useCallback((dto: Partial<Lead>) => {
    const structural = 'status' in dto || 'assignedTo' in dto
    return leadsApi.update(companyId!, leadId!, dto).then(() => invalidateAll(structural))
  }, [companyId, leadId])

  const deleteMutation = useMutation({
    mutationFn: () => leadsApi.remove(companyId!, leadId!),
    onSuccess: () => {
      toast.success('Lead deleted')
      // Invalidate cached lists so the user lands on /leads with a fresh
      // fetch — same pattern as DealDetail.
      qc.invalidateQueries({ queryKey: ['leads', companyId] })
      navigate(`/company/${companyId}/leads`)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete lead')
    },
  })

  if (isLoading || !lead) return <PageSpinner />

  const leadStatuses = (statuses ?? []).filter(s => s.applyTo === 'lead')

  // Status picker labels: "Not Answered (165 leads)". A status with no leads
  // still lists, at 0 — the absence of a row means zero, not unknown.
  const countByStatus = new Map((statusCounts ?? []).map(s => [s.status, s.count]))
  const totalLeadCount = (statusCounts ?? []).reduce((n, s) => n + s.count, 0)
  const countLabel = (n: number) => `${n} ${n === 1 ? 'lead' : 'leads'}`
  const navStatusOptions = [
    { value: '', label: `All statuses (${countLabel(totalLeadCount)})` },
    ...leadStatuses.map(s => ({
      value: s.label,
      label: `${s.label} (${countLabel(countByStatus.get(s.label) ?? 0)})`,
    })),
  ]

  const orgOptions = [
    { value: '', label: '— None —' },
    ...(orgsData ?? []).map(o => ({ value: o.id, label: o.name })),
  ]
  const campaignOrgName = lead.organizationId
    ? ((orgsData ?? []).find(o => o.id === lead.organizationId)?.name ?? fallbackOrg?.name ?? null)
    : null

  return (
    <div className="flex flex-col h-full">
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteMutation.mutate()}
        title="Delete this lead?"
        message={`"${fullName(lead.firstName, lead.lastName)}" and all its activity will be permanently deleted.`}
        confirmLabel="Delete Lead"
        isPending={deleteMutation.isPending}
      />

      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/company/${companyId}/leads`)} className="btn btn-ghost btn-sm p-1.5">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="page-title">{fullName(lead.firstName, lead.lastName)}</h1>
          <SLATimer deadline={lead.slaDeadline} breached={lead.slaBreahed} />
          {lead.convertedToDealId && <span className="badge badge-green">Converted</span>}
        </div>
        <div className="flex items-center gap-2">
          {/* Fixed width: the panel is left-0/right-0 on this container, so
              without it both the button and the list shrink to the flex row
              and truncate labels like "Not Interested (167 leads)". */}
          <DropdownSelect
            value={navStatus}
            onChange={setNavStatus}
            options={navStatusOptions}
            className="w-64 flex-shrink-0"
          />
          <button
            onClick={() => prevData?.id && navigate(`/company/${companyId}/leads/${prevData.id}`)}
            disabled={prevData?.id === null}
            title="Previous lead"
            className="btn btn-ghost btn-sm p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => nextData?.id && navigate(`/company/${companyId}/leads/${nextData.id}`)}
            disabled={nextData?.id === null}
            title="Next lead"
            className="btn btn-ghost btn-sm p-1.5 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => setConvertOpen(true)}
            disabled={!!lead.convertedToDealId}
            className="btn btn-secondary btn-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Convert
          </button>
          {lead.phone && (
            <button
              onClick={async () => {
                const target = canSeePhone
                  ? lead.phone!
                  : decodeRt((await leadsApi.rt(companyId!, leadId!)).data.v)
                startCall(target, fullName(lead.firstName, lead.lastName), companyId!, leadId!)
              }}
              title={canSeePhone ? `Call ${lead.phone}` : 'Call'}
              className="btn btn-ghost btn-sm text-gray-400 hover:text-green-500"
            >
              <Phone className="w-4 h-4" />
            </button>
          )}
          {!isSalesUser && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="btn btn-ghost btn-sm text-red-600"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Body: 60/40 layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Timeline */}
        <div className="flex-[3] overflow-y-auto px-6 py-4 space-y-4">
          <ComposeArea companyId={companyId!} refType="leads" refId={leadId!} />

          <ActivityTimeline
            companyId={companyId!}
            refType="leads"
            refId={leadId!}
            calls={leadCalls ?? []}
          />
        </div>

        {/* Right: Fields */}
        <div className="flex-[2] overflow-y-auto border-l border-gray-200 bg-white px-5 py-4 space-y-5">

          {/* Status */}
          <div>
            <p className="section-title mb-2">Status</p>
            <StatusSelect
              value={lead.status}
              statuses={leadStatuses}
              onChange={val => patch({ status: val })}
            />
          </div>

          {/* Assigned To */}
          <div>
            <p className="section-title mb-1">Assignment</p>
            <dl>
              <AssignedToField
                value={lead.assignedTo}
                users={users ?? []}
                onAssign={userId => patch({ assignedTo: userId })}
                onUnassign={() => patch({ assignedTo: null as any })}
                readOnly={isSalesUser}
              />
            </dl>
          </div>

          {/* Details */}
          <div>
            <p className="section-title mb-1">Details</p>
            <dl className="divide-y divide-gray-50">
              <InlineField
                label="First Name" value={lead.firstName}
                onSave={v => patch({ firstName: v })}
                readOnly={isSalesUser}
              />
              <InlineField
                label="Last Name" value={lead.lastName}
                onSave={v => patch({ lastName: v })}
                readOnly={isSalesUser}
              />
              <InlineField
                label="Email"
                value={canSeeEmail ? (lead.email ?? '') : (lead.email ? maskEmail(lead.email) : '')}
                type={canSeeEmail ? 'email' : 'text'}
                onSave={v => patch({ email: v })}
                placeholder="—"
                readOnly={isSalesUser}
              />
              {canSeePhone ? (
                <InlineField
                  label="Phone" value={lead.phone ?? ''} type="tel"
                  onSave={v => patch({ phone: v })}
                  placeholder="—"
                />
              ) : (
                <InlineField
                  label="Phone"
                  value={lead.phone
                    ? (lead.phone.length <= 6
                        ? '•'.repeat(lead.phone.length)
                        : lead.phone.slice(0, 4) + '•'.repeat(lead.phone.length - 6) + lead.phone.slice(-2))
                    : '—'}
                  onSave={() => {}} readOnly
                />
              )}
              <InlineSelectField
                label="Organization"
                value={lead.organizationId ?? ''}
                displayValue={campaignOrgName}
                options={orgOptions}
                onSave={v => patch({ organizationId: v || null })}
                placeholder="— None —"
                readOnly={isSalesUser}
              />
              <InlineField
                label="Source" value={lead.source ?? ''}
                displayNode={lead.source ? <SourceBadge source={lead.source} /> : undefined}
                onSave={v => patch({ source: v })}
                placeholder="—"
                readOnly={isSalesUser}
              />
              <InlineField
                label="Created" value={formatDate(lead.createdAt)}
                onSave={() => {}} readOnly
              />
              <InlineField
                label="Updated" value={formatDate(lead.updatedAt)}
                onSave={() => {}} readOnly
              />
            </dl>
          </div>

          {/* Custom Fields */}
          {customFieldDefs && customFieldDefs.length > 0 && (
            <div>
              <p className="section-title mb-1">Custom Fields</p>
              <dl className="divide-y divide-gray-50">
                {customFieldDefs.map((field: CustomFieldDef) => {
                  const rawVal = (lead.customFields ?? {})[field.fieldKey]
                  const strVal = rawVal != null ? String(rawVal) : ''
                  const isReadOnly = field.isFromSource || isSalesUser

                  if (field.fieldType === 'select' && field.options?.length) {
                    return (
                      <InlineSelectField
                        key={field.fieldKey}
                        label={field.label}
                        value={strVal}
                        options={field.options.map(o => ({ value: o.value, label: o.label }))}
                        onSave={v => patch({ customFields: { ...(lead.customFields ?? {}), [field.fieldKey]: v } })}
                        readOnly={isReadOnly}
                      />
                    )
                  }

                  const inputType = field.fieldType === 'number' || field.fieldType === 'decimal' ? 'number'
                    : field.fieldType === 'date' ? 'date'
                    : field.fieldType === 'datetime' ? 'datetime-local'
                    : field.fieldType === 'email' ? 'email'
                    : field.fieldType === 'url' ? 'url'
                    : 'text'

                  return (
                    <InlineField
                      key={field.fieldKey}
                      label={field.label}
                      value={strVal}
                      type={inputType as any}
                      onSave={v => patch({ customFields: { ...(lead.customFields ?? {}), [field.fieldKey]: v } })}
                      readOnly={isReadOnly}
                    />
                  )
                })}
              </dl>
            </div>
          )}

          {/* Notes — auto-saving textarea */}
          <NotesField
            companyId={companyId!}
            leadId={leadId!}
            initialValue={lead.notes ?? ''}
          />
        </div>
      </div>

      {convertOpen && (
        <ConvertModal
          open={convertOpen}
          onClose={() => setConvertOpen(false)}
          companyId={companyId!}
          leadId={leadId!}
          leadName={fullName(lead.firstName, lead.lastName)}
          onSuccess={(dealId) => {
            setConvertOpen(false)
            // Invalidate everything affected by the conversion: the source
            // lead now has convertedAt, the leads list has updated status,
            // a fresh deal exists, and the dashboard wonLeads / avg-time
            // metrics need to recompute.
            qc.invalidateQueries({ queryKey: ['lead', leadId] })
            qc.invalidateQueries({ queryKey: ['leads', companyId] })
            qc.invalidateQueries({ queryKey: ['deals', companyId] })
            qc.invalidateQueries({ queryKey: ['dashboard-overview', companyId] })
            navigate(`/company/${companyId}/deals/${dealId}`)
          }}
        />
      )}
    </div>
  )
}

// ── Convert modal ─────────────────────────────────────────────────────────────

interface ConvertFormData {
  dealTitle: string; createNewContact: boolean
}

function ConvertModal({ open, onClose, companyId, leadId, leadName, onSuccess }: {
  open: boolean; onClose: () => void; companyId: string; leadId: string
  leadName: string; onSuccess: (dealId: string) => void
}) {
  const { register, handleSubmit } = useForm<ConvertFormData>({
    defaultValues: { dealTitle: `Deal — ${leadName}`, createNewContact: true },
  })
  const mutation = useMutation({
    mutationFn: (dto: ConvertFormData) =>
      leadsApi.convert(companyId, leadId, dto as unknown as Record<string, unknown>).then(r => r.data),
    onSuccess: (deal: { id: string }) => { toast.success('Converted to deal'); onSuccess(deal.id) },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to convert lead')
    },
  })
  return (
    <Modal open={open} onClose={onClose} title="Convert Lead to Deal" size="sm">
      <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
        <div><label className="label">Deal Title</label><input {...register('dealTitle', { required: true })} className="input" /></div>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input type="checkbox" {...register('createNewContact')} className="rounded" />
            Create new contact from lead
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
          <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Convert</button>
        </div>
      </form>
    </Modal>
  )
}
